"use client";

import React, { useContext, useEffect, useRef, useState } from "react";
import type { FormState, UseFormReturn } from "react-hook-form";
import { isDeepEqual } from "src/lib/MiscFrontendUtils";

// The form context and its hooks, kept apart from Form so components that only read form state
// (such as Button, which is on every page) don't load react-hook-form.

export type FormContextValue = Omit<UseFormReturn, "formState"> & {
  getFormState: () => FormState<any>;
  value?: any;
};

export const FormContext = React.createContext<FormContextValue | undefined>(undefined);

export const useForm = () => {
  const form = useContext(FormContext);
  return form;
};

interface Subject<T> {
  subscribe: (observer: { next: (value: T) => void }) => {
    unsubscribe: () => void;
  };
}

type SubscribeProps<T> = {
  disabled?: boolean;
  subject: Subject<T>;
  next: (value: T) => void;
};

export function useSubscribe<T>(props: SubscribeProps<T>) {
  const _props = useRef(props);
  _props.current = props;

  useEffect(() => {
    const subscription =
      !props.disabled &&
      _props.current.subject &&
      _props.current.subject.subscribe({
        next: _props.current.next,
      });

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [props.disabled]);
}

export const useFormState = <R>(selector?: (state: FormState<any>) => R): R => {
  const form = useContext(FormContext);

  const selectState = (newState?: any) => {
    if (!form) {
      return undefined;
    }
    const state: any = {};
    const formState = form.getFormState();
    for (const key of Object.getOwnPropertyNames(formState)) {
      state[key] = (formState as any)[key];
    }
    Object.assign(state, newState);

    if (selector) {
      return selector(state);
    }
    return state;
  };

  const [state, setState] = useState<R>(() => selectState());

  const maybeUpdate = (stateUpdate?: any) => {
    const newState = selectState(stateUpdate);
    if (isDeepEqual(newState, state)) {
      return;
    }
    setState(newState);
  };

  // Force refresh when form reference changes
  useEffect(() => {
    maybeUpdate();
  }, [form]);

  useSubscribe({
    next: (stateUpdate) => {
      maybeUpdate(stateUpdate);
    },
    // @ts-expect-error
    subject: form?.control._subjects.state,
  });

  return state;
};
